/**
 * URL parsing for the link-attachment form.
 *
 * Lifted out of `ExtrasManager.tsx` (1030 lines, 13 useState, 8 useEffect, zero
 * tests). Pure, so it moves with no behavioural risk — and it is worth testing
 * on its own because it gates both live validation and the favicon/preview UI,
 * and its edge cases (missing scheme, hostname with no dot) are the kind that
 * are easy to regress and hard to notice.
 */

/**
 * The hostname of `raw`, with a leading `www.` stripped, or `null` if it does
 * not parse as a plausible host.
 */
export function parseHostname(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Tolerate `example.com` pasted without a scheme. The backend preview
  // endpoint prepends https:// in that case, so mirroring it here keeps
  // client-side validation and the server's behaviour in agreement.
  const candidate = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const u = new URL(candidate);
    // `new URL('https://localhost')` parses happily, but a host with no dot is
    // almost always a typo in this context, so it is rejected rather than sent
    // to the preview endpoint.
    if (!u.hostname.includes('.')) return null;
    return u.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}
